/**
 * Privacy Policy Page - Chính sách bảo mật
 */

import { UnifiedHeader } from '../../components/UnifiedHeader';
import { UnifiedFooter } from '../../components/UnifiedFooter';
import { SEOHead } from '../../components/SEOHead';
import { getCanonicalUrl, getSiteUrl } from '../../utils/siteConfig';
import { StructuredData } from '../../components/StructuredData';
import { Shield, Database, Eye, Lock, UserCheck, Mail } from 'lucide-react';
import { motion } from 'framer-motion';

export function PrivacyPolicyPage() {
  return (
    <div className='bg-background-light dark:bg-background-dark text-text-main-light dark:text-text-main-dark font-body transition-colors duration-200 min-h-screen flex flex-col'>
      <SEOHead
        title='Chính sách bảo mật | Hướng nghiệp CAT'
        description='Chính sách bảo mật thông tin cá nhân tại Hướng nghiệp CAT'
        canonicalUrl={getCanonicalUrl('/chinh-sach-bao-mat')}
      />
      <StructuredData
        data={{
          type: 'BreadcrumbList',
          items: [
            { name: 'Trang chủ', url: getSiteUrl() },
            { name: 'Chính sách bảo mật', url: getCanonicalUrl('/chinh-sach-bao-mat') },
          ],
        }}
      />
      <UnifiedHeader />

      <motion.main
        className='flex-1 pt-20 pb-12'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          {/* Header */}
          <div className='text-center mb-12'>
            <h1 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
              Chính sách bảo mật
            </h1>
            <p className='text-lg text-gray-600 dark:text-gray-300'>
              Cập nhật lần cuối: Tháng 12/2024
            </p>
          </div>

          <div className='space-y-8'>
            {/* Intro */}
            <div className='bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-6 border border-blue-200 dark:border-blue-800'>
              <div className='flex items-start gap-4'>
                <Shield className='w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1' />
                <div>
                  <h3 className='font-semibold text-blue-800 dark:text-blue-300 mb-2'>
                    Cam kết bảo mật
                  </h3>
                  <p className='text-blue-700 dark:text-blue-400'>
                    Hướng nghiệp CAT cam kết bảo vệ quyền riêng tư và thông tin cá nhân của bạn.
                    Chính sách này giải thích cách chúng tôi thu thập, sử dụng và bảo vệ dữ liệu của
                    bạn.
                  </p>
                </div>
              </div>
            </div>

            {/* Data Collection */}
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8'>
              <div className='flex items-center gap-3 mb-6'>
                <div className='w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center'>
                  <Database className='w-6 h-6 text-purple-600 dark:text-purple-400' />
                </div>
                <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
                  Thông tin chúng tôi thu thập
                </h2>
              </div>

              <div className='space-y-4'>
                <div className='p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl'>
                  <h3 className='font-semibold text-gray-900 dark:text-white mb-2'>
                    Thông tin đăng ký tài khoản
                  </h3>
                  <ul className='text-gray-600 dark:text-gray-300 space-y-1'>
                    <li>• Họ tên</li>
                    <li>• Địa chỉ email</li>
                    <li>• Số điện thoại (nếu cung cấp)</li>
                    <li>• Cấp học (THPT/THCS)</li>
                  </ul>
                </div>

                <div className='p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl'>
                  <h3 className='font-semibold text-gray-900 dark:text-white mb-2'>
                    Dữ liệu sử dụng dịch vụ
                  </h3>
                  <ul className='text-gray-600 dark:text-gray-300 space-y-1'>
                    <li>• Kết quả bài thi và trắc nghiệm</li>
                    <li>• Lịch sử làm bài</li>
                    <li>• Thông tin thanh toán (được mã hóa)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Data Usage */}
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8'>
              <div className='flex items-center gap-3 mb-6'>
                <div className='w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center'>
                  <Eye className='w-6 h-6 text-green-600 dark:text-green-400' />
                </div>
                <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
                  Mục đích sử dụng thông tin
                </h2>
              </div>

              <ul className='space-y-3'>
                {[
                  'Cung cấp dịch vụ trắc nghiệm hướng nghiệp và thi thử',
                  'Tạo báo cáo phân tích năng lực và xu hướng nghề nghiệp',
                  'Xử lý thanh toán và giao dịch',
                  'Gửi thông báo về tài khoản và dịch vụ',
                  'Cải thiện chất lượng dịch vụ và trải nghiệm người dùng',
                  'Hỗ trợ khách hàng khi có yêu cầu',
                ].map((item, index) => (
                  <li key={index} className='flex items-start gap-3'>
                    <div className='w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0' />
                    <span className='text-gray-600 dark:text-gray-300'>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Data Protection */}
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8'>
              <div className='flex items-center gap-3 mb-6'>
                <div className='w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center'>
                  <Lock className='w-6 h-6 text-amber-600 dark:text-amber-400' />
                </div>
                <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
                  Bảo vệ thông tin
                </h2>
              </div>

              <div className='prose dark:prose-invert max-w-none'>
                <p className='text-gray-600 dark:text-gray-300 mb-4'>
                  Chúng tôi áp dụng các biện pháp bảo mật tiên tiến:
                </p>

                <ul className='space-y-2 text-gray-600 dark:text-gray-300'>
                  <li className='flex items-start gap-2'>
                    <span className='text-amber-500'>🔒</span>
                    Mã hóa SSL/TLS cho tất cả kết nối
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-amber-500'>🔒</span>
                    Mã hóa dữ liệu nhạy cảm trong cơ sở dữ liệu
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-amber-500'>🔒</span>
                    Hệ thống xác thực bảo mật đa lớp
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-amber-500'>🔒</span>
                    Sao lưu dữ liệu định kỳ
                  </li>
                  <li className='flex items-start gap-2'>
                    <span className='text-amber-500'>🔒</span>
                    Không chia sẻ thông tin với bên thứ ba nếu không có sự đồng ý
                  </li>
                </ul>
              </div>
            </div>

            {/* User Rights */}
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8'>
              <div className='flex items-center gap-3 mb-6'>
                <div className='w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center'>
                  <UserCheck className='w-6 h-6 text-blue-600 dark:text-blue-400' />
                </div>
                <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>Quyền của bạn</h2>
              </div>

              <div className='grid md:grid-cols-2 gap-4'>
                {[
                  { title: 'Truy cập dữ liệu', desc: 'Xem thông tin cá nhân đã cung cấp' },
                  { title: 'Chỉnh sửa', desc: 'Cập nhật thông tin tài khoản' },
                  { title: 'Xóa tài khoản', desc: 'Yêu cầu xóa toàn bộ dữ liệu' },
                  { title: 'Từ chối email', desc: 'Hủy đăng ký nhận thông báo quảng cáo' },
                ].map(({ title, desc }, index) => (
                  <div key={index} className='p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl'>
                    <h3 className='font-semibold text-gray-900 dark:text-white mb-1'>{title}</h3>
                    <p className='text-gray-600 dark:text-gray-300 text-sm'>{desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className='bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-6'>
              <div className='flex items-center gap-3 mb-3'>
                <Mail className='w-5 h-5 text-gray-600 dark:text-gray-400' />
                <h3 className='font-semibold text-gray-900 dark:text-white'>
                  Liên hệ về quyền riêng tư
                </h3>
              </div>
              <p className='text-gray-600 dark:text-gray-300'>
                Nếu có câu hỏi về chính sách bảo mật hoặc muốn thực hiện quyền của mình, vui lòng
                liên hệ: <strong>support@huongnghiep.cat.edu.vn</strong>
              </p>
            </div>
          </div>
        </div>
      </motion.main>

      <UnifiedFooter />
    </div>
  );
}

export default PrivacyPolicyPage;
